import { forwardRef, useEffect, useState, useRef } from "react";

import classnames from "classnames";
import { isNotEmpty, noop } from "neetocist";
import useOnClickOutside from "neetocommons/react-utils/useOnClickOutside";
import useStateWithDependency from "neetocommons/react-utils/useStateWithDependency";
import useHotkeys from "neetohotkeys";
import { Check, Close } from "neetoicons";
import { Button, Input } from "neetoui";
import PropTypes from "prop-types";
import { isNotNil } from "ramda";
import { useTranslation } from "react-i18next";

import { TOOLTIP_PROPS } from "./constants";
import "./inlineinput.scss";
import { mergeRefs } from "./utils";

const InlineInput = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const {
    saveOnBlur = false,
    clearOnSave = false,
    nullable = false,
    value = "",
    isSaving,
    className = "",
    handleSubmit = noop,
    handleCancel = noop,
    submitButtonProps = {},
    cancelButtonProps = {},
    dirty,
    onInlineInputBlur = noop,
    size = "medium",
    ...otherProps
  } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [internalValue, setInternalValue] = useStateWithDependency(value ?? "");
  const inputRef = useRef(null);
  const inputWrapperRef = useRef(null);
  const trimmedValue = internalValue && internalValue?.trim();
  const isValidValue =
    nullable || (isNotEmpty(trimmedValue) && isNotNil(trimmedValue));

  const isDirty = internalValue !== value || dirty;

  const handleFocusOut = () => {
    if (!isValidValue || isSubmitting) return;

    onInlineInputBlur(trimmedValue);

    if (saveOnBlur && isDirty) {
      onSubmit();

      return;
    }

    onCancel();
  };

  useOnClickOutside(inputWrapperRef, handleFocusOut);

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      await handleSubmit(trimmedValue);
      clearOnSave && setInternalValue("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancel = () => {
    handleCancel(trimmedValue);
  };

  const closeRef = useHotkeys(
    "escape",
    event => {
      event.stopPropagation();
      onCancel();
    },
    { mode: "scoped" }
  );

  const submitRef = useHotkeys(["command+return", "return"], () => onSubmit(), {
    mode: "scoped",
    enabled: isValidValue,
  });

  useEffect(() => inputRef.current && inputRef.current.focus(), [isSubmitting]);

  return (
    <div
      className={classnames("flex flex-grow", { [className]: className })}
      ref={inputWrapperRef}
    >
      <Input
        {...{ size }}
        disableTrimOnBlur
        aria-label={t("neetoMolecules.inlineInput.editableField")}
        className="neeto-molecules-autosave-input"
        data-testid="neeto-molecules-autosave-input"
        disabled={isSubmitting}
        ref={mergeRefs(inputRef, closeRef, submitRef)}
        value={internalValue}
        {...otherProps}
        suffix={
          <>
            <Button
              {...{ size }}
              aria-label={t("neetoMolecules.common.actions.save")}
              data-testid="neeto-molecules-autosave-input-save"
              icon={Check}
              loading={isSubmitting || isSaving}
              style="text"
              tooltipProps={TOOLTIP_PROPS["save"]}
              onClick={onSubmit}
              {...submitButtonProps}
              disabled={
                !isValidValue ||
                isSubmitting ||
                isSaving ||
                submitButtonProps.disabled ||
                !isDirty
              }
            />
            <Button
              {...{ ref, size }}
              aria-label={t("neetoMolecules.common.actions.cancel")}
              data-testid="neeto-molecules-autosave-input-cancel"
              icon={Close}
              style="text"
              tooltipProps={TOOLTIP_PROPS["cancel"]}
              onClick={onCancel}
              {...cancelButtonProps}
              disabled={isSubmitting || isSaving || cancelButtonProps.disabled}
            />
          </>
        }
        onChange={e => {
          otherProps?.onChange?.(e);
          setInternalValue(e.target.value);
        }}
      />
    </div>
  );
});

InlineInput.displayName = "InlineInput";

InlineInput.propTypes = {
  /**
   * To provide additional classes to the component.
   */
  className: PropTypes.string,
  /**
   * To specify the text to be displayed in the component.
   */
  value: PropTypes.string,
  /**
   * To enable submission of empty values.
   */
  nullable: PropTypes.bool,
  /**
   * To specify whether the value is being saved.
   */
  isSaving: PropTypes.bool,
  /**
   * To specify whether to save the value on blur.
   */
  saveOnBlur: PropTypes.bool,
  /**
   * To specify whether to clear the input on save.
   */
  clearOnSave: PropTypes.bool,
  /**
   * To specify additional props for submit button.
   */
  submitButtonProps: PropTypes.object,
  /**
   * To specify additional props for cancel button.
   */
  cancelButtonProps: PropTypes.object,
  /**
   * Function to be called on clicking the submit button.
   */
  handleSubmit: PropTypes.func,
  /**
   * Function to be called on clicking the cancel button .
   */
  handleCancel: PropTypes.func,
  /**
   * Size of the input field and buttons.
   * Options: "small", "medium", "large"
   */
  size: PropTypes.oneOf(["small", "medium", "large"]),
};

export default InlineInput;
